/**
 * @fileoverview Product Analytics - Performance Monitoring
 * @module features/analytics/performance
 * 
 * @description
 * Track web performance metrics including Core Web Vitals,
 * page load times, and resource performance.
 * 
 * @useCases
 * - Monitor Core Web Vitals (LCP, FID, CLS)
 * - Track page load performance
 * - Identify slow pages and resources
 * - Correlate performance with conversions
 * - Monitor API response times
 * 
 * @metrics
 * - Largest Contentful Paint (LCP)
 * - First Input Delay (FID)
 * - Cumulative Layout Shift (CLS)
 * - Time to First Byte (TTFB)
 * - First Contentful Paint (FCP)
 * 
 * @chartData
 * - Line chart: Core Web Vitals over time
 * - Bar chart: Slowest pages
 * - Histogram: Load time distribution
 * - Gauge: Performance score
 * 
 * @example
 * ```typescript
 * const performance = new Performance(webId, endpoint, apiKey);
 * 
 * // Track Core Web Vitals
 * await performance.trackWebVitals({
 *   lcp: 2500,
 *   fid: 100,
 *   cls: 0.1,
 *   pageUrl: '/products'
 * });
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { DateRangeFilter, TimeSeriesData } from "../../types";
import { getWindow, isBrowser, combineTeardowns, Teardown } from "../../utils/browser";

// ============================================================================
// CONFIGURATION TYPES
// ============================================================================

/**
 * Core Web Vitals data.
 */
export interface WebVitalsData {
  /** Largest Contentful Paint (ms) */
  lcp?: number;
  /** First Input Delay (ms) */
  fid?: number;
  /** Cumulative Layout Shift (score) */
  cls?: number;
  /** First Contentful Paint (ms) */
  fcp?: number;
  /** Time to First Byte (ms) */
  ttfb?: number;
  /** Interaction to Next Paint (ms) */
  inp?: number;
  /** Page URL */
  pageUrl: string;
  /** User ID */
  userId?: string;
  /** Connection type */
  connectionType?: 'slow-2g' | '2g' | '3g' | '4g' | 'wifi' | 'unknown';
  /** Device type */
  deviceType?: 'mobile' | 'tablet' | 'desktop';
}

/**
 * Page load timing data.
 */
export interface PageLoadData {
  /** Page URL */
  pageUrl: string;
  /** DNS lookup time (ms) */
  dnsTime?: number;
  /** TCP connection time (ms) */
  tcpTime?: number;
  /** Time to first byte (ms) */
  ttfb?: number;
  /** DOM content loaded time (ms) */
  domContentLoaded?: number;
  /** Full page load time (ms) */
  loadTime?: number;
  /** DOM interactive time (ms) */
  domInteractive?: number;
  /** Number of resources */
  resourceCount?: number;
  /** Total transfer size (bytes) */
  transferSize?: number;
}

/**
 * Resource timing data.
 */
export interface ResourceData {
  /** Resource URL */
  resourceUrl: string;
  /** Resource type */
  resourceType: 'script' | 'stylesheet' | 'image' | 'font' | 'fetch' | 'xhr' | 'other';
  /** Duration (ms) */
  duration: number;
  /** Transfer size (bytes) */
  transferSize?: number;
  /** Page URL where resource was loaded */
  pageUrl: string;
}

/**
 * Performance response from API.
 */
export interface PerformanceResponse {
  eventName: string;
  properties: Record<string, unknown>;
  response: {
    recorded: boolean;
    performanceScore?: number;
  };
  timestamp: string;
}

/**
 * Performance analytics data.
 */
export interface PerformanceAnalyticsData {
  /** Core Web Vitals summary */
  webVitals: {
    lcp: { p50: number; p75: number; p95: number; good: number; needsImprovement: number; poor: number };
    fid: { p50: number; p75: number; p95: number; good: number; needsImprovement: number; poor: number };
    cls: { p50: number; p75: number; p95: number; good: number; needsImprovement: number; poor: number };
    inp: { p50: number; p75: number; p95: number; good: number; needsImprovement: number; poor: number };
  };
  /** Performance by page */
  byPage: Array<{
    pageUrl: string;
    avgLoadTime: number;
    avgLCP: number;
    avgFID: number;
    avgCLS: number;
    sampleSize: number;
  }>;
  /** Performance by device */
  byDevice: Array<{
    device: string;
    avgLoadTime: number;
    avgLCP: number;
    sampleSize: number;
  }>;
  /** Slowest resources */
  slowResources: Array<{
    resourceUrl: string;
    resourceType: string;
    avgDuration: number;
    occurrences: number;
  }>;
  /** Performance trends */
  trends: {
    lcp: TimeSeriesData;
    fid: TimeSeriesData;
    cls: TimeSeriesData;
  };
  /** Overall score (0-100) */
  performanceScore: number;
}

// ============================================================================
// PERFORMANCE CLASS
// ============================================================================

/**
 * Performance - Track web performance metrics.
 * 
 * @extends FlowGridTransport
 * 
 * @description
 * Provides performance monitoring including:
 * - Core Web Vitals tracking
 * - Page load timing
 * - Resource performance
 * - API response times
 * - Performance scoring
 * 
 * @example
 * ```typescript
 * const performance = new Performance(webId, endpoint, apiKey);
 * 
 * // Track Web Vitals (use web-vitals library)
 * import { onLCP, onFID, onCLS } from 'web-vitals';
 * 
 * onLCP((metric) => {
 *   performance.trackWebVitals({
 *     lcp: metric.value,
 *     pageUrl: window.location.pathname
 *   });
 * });
 * ```
 */
export class PerformanceTracking extends FlowGridTransport {
  /**
   * Tracks Core Web Vitals.
   * 
   * @param data - Web Vitals data
   * @returns Promise resolving to performance response
   */
  async trackWebVitals(data: WebVitalsData): Promise<PerformanceResponse> {
    return await this.init("web_vitals", {
      type: "web_vitals",
      ...data,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks page load timing.
   * 
   * @param data - Page load data
   * @returns Promise resolving to performance response
   */
  async trackPageLoad(data: PageLoadData): Promise<PerformanceResponse> {
    return await this.init("page_load", {
      type: "page_load",
      ...data,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks resource performance.
   * 
   * @param data - Resource data
   * @returns Promise resolving to performance response
   */
  async trackResource(data: ResourceData): Promise<PerformanceResponse> {
    return await this.init("resource_timing", {
      type: "resource_timing",
      ...data,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Tracks API call performance.
   * 
   * @param endpoint - API endpoint
   * @param duration - Response time (ms)
   * @param success - Whether call succeeded
   * @param statusCode - HTTP status code
   * @returns Promise resolving to performance response
   */
  async trackApiCall(
    endpoint: string,
    duration: number,
    success: boolean,
    statusCode?: number
  ): Promise<PerformanceResponse> {
    return await this.init("api_timing", {
      type: "api_timing",
      endpoint,
      duration,
      success,
      statusCode,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Auto-collect Core Web Vitals, navigation timing, and resource timing
   * directly from the browser. Safe no-op outside the browser.
   *
   * @param options.userId       Optional user identifier attached to every metric.
   * @param options.resources    When true, also subscribes to PerformanceObserver
   *                             for `resource` entries. Default: false (noisy).
   * @param options.minDuration  Only forward resources slower than this (ms). Default 200.
   * @returns A teardown function that detaches all observers/listeners.
   *
   * @example
   * ```ts
   * const stop = fg.performance.client.autoTrack({ userId: "u_1" });
   * // ...later
   * stop();
   * ```
   */
  autoTrack(options: {
    userId?: string;
    resources?: boolean;
    minDuration?: number;
  } = {}): Teardown {
    if (!isBrowser()) return () => { /* no-op on the server */ };

    const win = getWindow()!;
    const pageUrl = win.location.pathname;
    const teardowns: Array<() => void> = [];

    // --- Web Vitals via the `web-vitals` library (dynamic import keeps it
    //     out of server bundles even if tree-shaking misses it).
    let cancelled = false;
    void import(/* webpackChunkName: "flowgrid-vitals" */ "web-vitals").then((wv) => {
      if (cancelled) return;
      const send = (metric: { name: string; value: number }) => {
        const key = metric.name.toLowerCase() as keyof Pick<WebVitalsData, "lcp" | "fid" | "cls" | "fcp" | "ttfb" | "inp">;
        const payload: WebVitalsData = { pageUrl, userId: options.userId };
        (payload[key] as number | undefined) = metric.value;
        void this.trackWebVitals(payload);
      };
      wv.onLCP?.(send);
      wv.onCLS?.(send);
      wv.onINP?.(send);
      wv.onFCP?.(send);
      wv.onTTFB?.(send);
      // FID was removed in web-vitals v4+, but we still surface it if present
      (wv as { onFID?: (cb: (m: { name: string; value: number }) => void) => void }).onFID?.(send);
    }).catch(() => { /* library missing — silently skip */ });
    teardowns.push(() => { cancelled = true; });

    // --- PerformanceNavigationTiming → trackPageLoad
    const reportNavigation = () => {
      const nav = win.performance?.getEntriesByType?.("navigation")?.[0] as
        PerformanceNavigationTiming | undefined;
      if (!nav) return;
      void this.trackPageLoad({
        pageUrl,
        dnsTime: Math.max(0, nav.domainLookupEnd - nav.domainLookupStart),
        tcpTime: Math.max(0, nav.connectEnd - nav.connectStart),
        ttfb: Math.max(0, nav.responseStart - nav.requestStart),
        domContentLoaded: nav.domContentLoadedEventEnd,
        loadTime: nav.loadEventEnd,
        domInteractive: nav.domInteractive,
        transferSize: nav.transferSize,
      });
    };
    if (win.document.readyState === "complete") {
      reportNavigation();
    } else {
      const onLoad = () => reportNavigation();
      win.addEventListener("load", onLoad, { once: true });
      teardowns.push(() => win.removeEventListener("load", onLoad));
    }

    // --- Resource timing (opt-in)
    if (options.resources && typeof win.PerformanceObserver === "function") {
      const min = options.minDuration ?? 200;
      const obs = new win.PerformanceObserver((list) => {
        for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
          if (entry.duration < min) continue;
          const initiator = entry.initiatorType as ResourceData["resourceType"];
          void this.trackResource({
            resourceUrl: entry.name,
            resourceType: ([
              "script", "stylesheet", "image", "font", "fetch", "xhr",
            ].includes(initiator) ? initiator : "other"),
            duration: Math.round(entry.duration),
            transferSize: entry.transferSize,
            pageUrl,
          });
        }
      });
      try {
        obs.observe({ type: "resource", buffered: true });
        teardowns.push(() => obs.disconnect());
      } catch { /* unsupported */ }
    }

    return combineTeardowns(...teardowns);
  }
}

export { PerformanceTracking as Performance };
export default PerformanceTracking;
